import { z } from 'zod'

/**
 * Inscricoes em eventos (RF03.1).
 *
 * O coordenador monta o formulario; a definicao vira JSON no banco. A mesma
 * funcao `buildRegistrationSubmitSchema` gera o validador Zod a partir dessa
 * definicao no servidor E no navegador - por isso e impossivel o front aceitar
 * algo que a API rejeita, ou o contrario.
 *
 * Nome, telefone e e-mail sao campos de sistema, nao campos do construtor:
 * ficam em colunas proprias para que busca, listagem, check-in e exportacao
 * funcionem sem depender de como o coordenador nomeou os campos.
 */

export const REGISTRATION_FIELD_TYPES = [
  'TEXT',
  'TEXTAREA',
  'EMAIL',
  'PHONE',
  'NUMBER',
  'DATE',
  'SELECT',
  'RADIO',
  'CHECKBOX_GROUP',
  'CONSENT',
] as const

export type RegistrationFieldType = (typeof REGISTRATION_FIELD_TYPES)[number]

export const REGISTRATION_FIELD_TYPE_LABELS: Record<RegistrationFieldType, string> = {
  TEXT: 'Texto curto',
  TEXTAREA: 'Texto longo',
  EMAIL: 'E-mail',
  PHONE: 'Telefone',
  NUMBER: 'Numero',
  DATE: 'Data',
  SELECT: 'Lista de selecao',
  RADIO: 'Escolha unica',
  CHECKBOX_GROUP: 'Multipla escolha',
  CONSENT: 'Aceite (caixa de marcacao)',
}

const fieldIdSchema = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Identificador tecnico invalido')

const optionSchema = z.object({
  value: z.string().min(1, 'Informe o valor').max(120),
  label: z.string().min(1, 'Informe o texto da opcao').max(160),
})

const baseFieldShape = {
  id: fieldIdSchema,
  label: z.string().min(1, 'Informe o rotulo do campo').max(160).trim(),
  help: z.string().max(300).trim().optional(),
  required: z.boolean().default(false),
}

const choiceFieldOptions = {
  options: z.array(optionSchema).min(2, 'Informe ao menos duas opcoes').max(50),
}

export const registrationFieldSchema = z.discriminatedUnion('type', [
  z.object({
    ...baseFieldShape,
    type: z.literal('TEXT'),
    maxLength: z.number().int().min(1).max(500).default(200),
  }),
  z.object({
    ...baseFieldShape,
    type: z.literal('TEXTAREA'),
    maxLength: z.number().int().min(1).max(4000).default(1000),
  }),
  z.object({ ...baseFieldShape, type: z.literal('EMAIL') }),
  z.object({ ...baseFieldShape, type: z.literal('PHONE') }),
  z.object({
    ...baseFieldShape,
    type: z.literal('NUMBER'),
    min: z.number().int().optional(),
    max: z.number().int().optional(),
  }),
  z.object({ ...baseFieldShape, type: z.literal('DATE') }),
  z.object({ ...baseFieldShape, type: z.literal('SELECT'), ...choiceFieldOptions }),
  z.object({ ...baseFieldShape, type: z.literal('RADIO'), ...choiceFieldOptions }),
  z.object({
    ...baseFieldShape,
    type: z.literal('CHECKBOX_GROUP'),
    ...choiceFieldOptions,
    maxSelections: z.number().int().min(1).max(50).optional(),
  }),
  z.object({ ...baseFieldShape, type: z.literal('CONSENT') }),
])

export type RegistrationField = z.infer<typeof registrationFieldSchema>

/** Configuracao minima necessaria para montar o validador de envio. */
export interface RegistrationFormConfig {
  fields: RegistrationField[]
  collectBirthDate: boolean
  requireGuardianForMinors: boolean
}

/**
 * Texto de consentimento usado quando o evento nao tem formulario proprio.
 *
 * Exportado porque o servidor precisa dele: ativar inscricoes e suficiente para
 * o evento receber inscritos, e nesse caso o consentimento gravado e este.
 */
export const DEFAULT_CONSENT_TEXT =
  'Autorizo a Paroquia de Pacaembu a utilizar os dados informados exclusivamente para a organizacao deste evento, conforme a Lei Geral de Protecao de Dados (LGPD).'

/**
 * Configuracao de formulario para evento que nao montou um.
 *
 * Existe para que "ativar inscricoes" baste. Exigir a criacao de um formulario
 * antes escondia o botao de inscricao no portal sem nenhuma explicacao - o
 * coordenador configurava tudo e o fiel nao via onde se inscrever.
 */
export const DEFAULT_FORM_CONFIG: RegistrationFormConfig & { consentText: string } = {
  fields: [],
  collectBirthDate: false,
  requireGuardianForMinors: false,
  consentText: DEFAULT_CONSENT_TEXT,
}

export const registrationFormInputSchema = z
  .object({
    fields: z.array(registrationFieldSchema).max(40, 'No maximo 40 campos'),
    /** Data de nascimento - necessaria para identificar inscrito menor de idade. */
    collectBirthDate: z.boolean().default(false),
    /** LGPD: exige responsavel legal quando o inscrito tem menos de 18 anos. */
    requireGuardianForMinors: z.boolean().default(true),
    /** Texto de consentimento exibido acima do botao de envio. */
    consentText: z
      .string()
      .min(20, 'Escreva o texto de consentimento')
      .max(2000)
      .trim()
      .default(DEFAULT_CONSENT_TEXT),
    /** Mensagem extra no comprovante. O aviso de pagamento presencial e fixo. */
    confirmationMessage: z.string().max(1000).trim().optional(),
  })
  .superRefine((data, ctx) => {
    const seen = new Set<string>()
    data.fields.forEach((field, index) => {
      if (seen.has(field.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index, 'id'],
          message: 'Identificador de campo repetido',
        })
      }
      seen.add(field.id)
    })
    if (data.requireGuardianForMinors && !data.collectBirthDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['collectBirthDate'],
        message: 'Para exigir responsavel legal e preciso coletar a data de nascimento',
      })
    }
  })

export type RegistrationFormInput = z.infer<typeof registrationFormInputSchema>


// --- Campos de sistema -----------------------------------------------------

/** Telefone brasileiro: normaliza para digitos e exige fixo (10) ou celular (11). */
export const brPhoneSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((v) => v.length === 10 || v.length === 11, {
    error: 'Informe o telefone com DDD, por exemplo (18) 99999-9999',
  })

const emptyToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value

/** Idade completa na data de referencia. */
export function ageOn(birthDate: string, reference: Date = new Date()): number {
  const [year, month, day] = birthDate.split('-').map(Number)
  if (!year || !month || !day) return Number.NaN
  let age = reference.getUTCFullYear() - year
  const monthDiff = reference.getUTCMonth() + 1 - month
  if (monthDiff < 0 || (monthDiff === 0 && reference.getUTCDate() < day)) age -= 1
  return age
}

export function isMinor(birthDate: string, reference: Date = new Date()): boolean {
  const age = ageOn(birthDate, reference)
  return Number.isFinite(age) && age < 18
}

// --- Construcao dinamica do validador -------------------------------------

function schemaForField(field: RegistrationField): z.ZodType {
  switch (field.type) {
    case 'TEXT':
    case 'TEXTAREA': {
      const base = z
        .string()
        .trim()
        .max(field.maxLength, 'Texto acima do limite de ' + field.maxLength + ' caracteres')
      return field.required ? base.min(1, 'Campo obrigatorio') : base.optional()
    }
    case 'EMAIL': {
      const base = z.email('Informe um e-mail valido').toLowerCase().trim()
      return field.required ? base : base.optional()
    }
    case 'PHONE':
      return field.required ? brPhoneSchema : brPhoneSchema.optional()
    case 'NUMBER': {
      let base = z.coerce.number({ error: 'Informe um numero' })
      if (field.min !== undefined) base = base.min(field.min, 'Valor minimo: ' + field.min)
      if (field.max !== undefined) base = base.max(field.max, 'Valor maximo: ' + field.max)
      return field.required ? base : base.optional()
    }
    case 'DATE': {
      const base = z.iso.date('Informe uma data valida')
      return field.required ? base : base.optional()
    }
    case 'SELECT':
    case 'RADIO': {
      const values = field.options.map((o) => o.value) as [string, ...string[]]
      const base = z.enum(values, { error: 'Selecione uma das opcoes' })
      return field.required ? base : base.optional()
    }
    case 'CHECKBOX_GROUP': {
      const values = field.options.map((o) => o.value) as [string, ...string[]]
      let base = z.array(z.enum(values, { error: 'Opcao invalida' }))
      if (field.required) base = base.min(1, 'Selecione ao menos uma opcao')
      if (field.maxSelections !== undefined) {
        base = base.max(field.maxSelections, 'Selecione no maximo ' + field.maxSelections)
      }
      return field.required ? base : base.default([])
    }
    case 'CONSENT':
      return field.required
        ? z.literal(true, { error: 'E preciso marcar este aceite para continuar' })
        : z.boolean().default(false)
  }
}

/** Validador do objeto de campos customizados do formulario. */
export function buildRegistrationDataSchema(fields: RegistrationField[]) {
  // Record mutavel, e nao z.ZodRawShape: no Zod 4 esse tipo tem index
  // signature somente leitura, entao a montagem campo a campo nao compila.
  const shape: Record<string, z.ZodType> = {}
  for (const field of fields) {
    shape[field.id] = z.preprocess(emptyToUndefined, schemaForField(field))
  }
  return z.object(shape)
}

/**
 * Validador completo do envio publico de inscricao: campos de sistema +
 * campos customizados + regras de LGPD para menores.
 */
export function buildRegistrationSubmitSchema(config: RegistrationFormConfig) {
  const dataSchema = buildRegistrationDataSchema(config.fields)

  return z
    .object({
      fullName: z.string().min(3, 'Informe o nome completo').max(160).trim(),
      email: z.preprocess(
        emptyToUndefined,
        z.email('Informe um e-mail valido').toLowerCase().trim().optional(),
      ),
      phone: brPhoneSchema,
      birthDate: z.preprocess(
        emptyToUndefined,
        config.collectBirthDate
          ? z.iso.date('Informe a data de nascimento')
          : z.iso.date().optional(),
      ),
      guardianName: z.preprocess(emptyToUndefined, z.string().max(160).trim().optional()),
      guardianPhone: z.preprocess(emptyToUndefined, brPhoneSchema.optional()),
      notes: z.preprocess(emptyToUndefined, z.string().max(1000).trim().optional()),
      data: dataSchema,
      /** LGPD: consentimento explicito, gravado com data e IP. */
      consent: z.literal(true, {
        error: 'E preciso aceitar o uso dos dados para concluir a inscricao',
      }),
      /**
       * Campo isca contra robos: invisivel na tela, preenchido significa envio
       * automatizado.
       *
       * ACEITA qualquer texto de proposito. Rejeitar aqui por validacao
       * devolveria 400 e ensinaria ao robo que este campo e a armadilha; o
       * servidor responde 201 falso e descarta em silencio.
       */
      trap: z.string().max(200).optional(),
    })
    .superRefine((value, ctx) => {
      if (!config.requireGuardianForMinors || !value.birthDate) return
      if (!isMinor(value.birthDate as string)) return

      if (!value.guardianName) {
        ctx.addIssue({
          code: 'custom',
          path: ['guardianName'],
          message: 'Inscrito menor de 18 anos: informe o nome do responsavel legal',
        })
      }
      if (!value.guardianPhone) {
        ctx.addIssue({
          code: 'custom',
          path: ['guardianPhone'],
          message: 'Inscrito menor de 18 anos: informe o telefone do responsavel',
        })
      }
    })
}

export type RegistrationSubmitInput = z.infer<ReturnType<typeof buildRegistrationSubmitSchema>>

// --- Gestao interna das inscricoes ----------------------------------------

export const registrationStatusSchema = z.enum([
  'CONFIRMADA',
  'LISTA_ESPERA',
  'CANCELADA',
  'PRESENTE',
])

export type RegistrationStatus = z.infer<typeof registrationStatusSchema>

export const REGISTRATION_STATUS_LABELS: Record<RegistrationStatus, string> = {
  CONFIRMADA: 'Confirmada',
  LISTA_ESPERA: 'Lista de espera',
  CANCELADA: 'Cancelada',
  PRESENTE: 'Presenca confirmada',
}

/** Status que ocupam vaga - base do controle de capacidade. */
export const SEAT_TAKING_STATUSES: readonly RegistrationStatus[] = ['CONFIRMADA', 'PRESENTE']

export const updateRegistrationSchema = z.object({
  status: registrationStatusSchema.optional(),
  /** Anotacao interna da secretaria, nunca exibida ao inscrito. */
  internalNotes: z.string().max(2000).trim().optional(),
  /** Registro do pagamento presencial (RF03.1: nao existe pagamento online). */
  paymentReceived: z.boolean().optional(),
  paymentNote: z.string().max(200).trim().optional(),
})

export const registrationListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50),
  status: registrationStatusSchema.optional(),
  q: z.string().max(120).trim().optional(),
  paymentReceived: z.coerce.boolean().optional(),
})
